import React from 'react';
import {Tooltip} from '@mui/material';
import type {ManifestationStatus} from 'services/metadata/types';
import {useLocalization} from 'localization';
import {FlexBox, Icon, Layout, Spacer, ToolButton} from 'components';
import {ConfirmButton} from 'components/button/ConfirmButton';
import type {ManifestationPanel} from '../types';
import {ManifestationMoveButton} from '../../ManifestationMoveButton';
import {ManifestationStatusButton} from '../../ManifestationStatusButton';
import {ManifestationDownload} from './ManifestationDownload';

type Props = {
  manifestationId: string;
  manifestationStatus: ManifestationStatus;
  miniMode: boolean;
  changeStatusDisabled?: boolean;
  deleteDisabled?: boolean;
  moveDisabled?: boolean;
  notesCount: number;
  mediaCount: number;
  infoCount: number;
  panel?: ManifestationPanel;
  onManifestationStatusPreview: (
    status: ManifestationStatus | undefined,
  ) => void;
  onManifestationStatusChanged: (status: ManifestationStatus) => void;
  onToggleManifestationPanel: (panel: ManifestationPanel) => void;
  onManifestationDelete: () => void;
  metaqUrl?: string;
  historyUrl?: string;
  ean?: string;
  trykkunderlagReserved?: boolean;
};

export const ManifestationButtons: React.FC<Props> = ({
  manifestationId,
  manifestationStatus,
  miniMode,
  changeStatusDisabled,
  moveDisabled,
  notesCount,
  mediaCount,
  infoCount,
  panel,
  onToggleManifestationPanel,
  onManifestationStatusPreview,
  onManifestationStatusChanged,
  onManifestationDelete,
  metaqUrl,
  ean,
  historyUrl,
  deleteDisabled,
  trykkunderlagReserved,
}) => {
  const {t} = useLocalization();

  return (
    <FlexBox right>
      <ManifestationStatusButton
        status={manifestationStatus}
        disabled={changeStatusDisabled}
        onStatusChangePreview={onManifestationStatusPreview}
        onStatusChanged={onManifestationStatusChanged}
        miniMode={miniMode}
      />
      <Spacer size={1} />
      <ToolButton
        title={
          <Layout horizontal adjustCenter>
            {t('page.metadata.manifestation.tools.files')}
            {trykkunderlagReserved ? (
              <Tooltip title={'Reservert mot trykkunderlag'}>
                <Icon icon={'RemoveCircle'} />
              </Tooltip>
            ) : null}
          </Layout>
        }
        icon="Files"
        badgeCount={mediaCount}
        checked={panel === 'files'}
        onClick={() => onToggleManifestationPanel('files')}
        iconOnly={miniMode}
      />
      <Spacer size={1} />
      <ToolButton
        title={t('page.metadata.manifestation.tools.notes')}
        icon="Comments"
        badgeCount={notesCount}
        checked={panel === 'notes'}
        onClick={() => onToggleManifestationPanel('notes')}
        iconOnly={miniMode}
      />
      <Spacer size={1} />
      <ManifestationMoveButton
        manifestationId={manifestationId}
        disabled={moveDisabled}
        miniMode={miniMode}
      />
      <Spacer size={1} />
      <ToolButton
        title={t('page.metadata.manifestation.tools.info')}
        icon="Info"
        badgeCount={infoCount}
        checked={panel === 'info'}
        onClick={() => onToggleManifestationPanel('info')}
        iconOnly={miniMode}
      />
      <Spacer size={1} />
      <ToolButton
        title={t('page.metadata.manifestation.tools.preview')}
        icon="Preview"
        onClick={() => window.open(metaqUrl, '_blank')}
        iconOnly={miniMode}
      />
      {historyUrl ? (
        <>
          <Spacer size={1} />
          <ToolButton
            title={t('page.metadata.manifestation.tools.history')}
            icon="History"
            onClick={() => window.open(historyUrl, '_blank')}
            iconOnly={miniMode}
          />
        </>
      ) : null}
      {ean ? (
        <>
          <ManifestationDownload ean={ean} miniMode={miniMode} />
        </>
      ) : null}
      <Spacer size={1} />
      <ConfirmButton
        doubleConfirm
        onConfirm={onManifestationDelete}
        iconOnly={miniMode}
        doubleConfirmMessage={t('general.deleteDoubleConfirm')}
        icon={'Delete'}
        title={t('general.delete')}
        prevent={
          deleteDisabled
            ? t('page.metadata.manifestation.delete.prevent')
            : undefined
        }
      />
    </FlexBox>
  );
};
